import type {
	FeaturedVideosCluster,
	FeaturedVideosClusterAppliedFilters,
	FeaturedVideosClusterCard,
	FeaturedVideosClusterProperty,
	FeaturedVideosClusterSection,
	FeaturedVideosClustersResponse
} from '~/core/entities/property';

import { extractErrorLogContext, useLogger } from '~/composables/useLogger';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';

const EMPTY_SECTION: FeaturedVideosClusterSection = {
	sectionTitle: '',
	clusters: []
};

function getFirstPropertyImageUrl(
	property?: FeaturedVideosClusterProperty
): string | null {
	const image = property?.images?.[0];
	return (
		image?.portalWatermarkUrl?.trim() || image?.watermarkUrl?.trim() || null
	);
}

export function mapFeaturedVideosCluster(
	cluster: FeaturedVideosCluster
): FeaturedVideosClusterCard | null {
	const cardTitle = cluster.cardTitle?.trim();
	const targetCommercialId = cluster.properties?.[0]?.commercialId?.trim();
	if (!cardTitle || !targetCommercialId) return null;

	const previewImages = (cluster.properties ?? [])
		.slice(0, 3)
		.map(getFirstPropertyImageUrl)
		.filter((url): url is string => Boolean(url));

	return {
		clusterId: cluster.clusterId,
		cardTitle,
		count: cluster.count ?? 0,
		cardVideo: cluster.cardVideo ?? null,
		previewImages,
		appliedFilters: cluster.appliedFilters ?? {},
		targetCommercialId
	};
}

export function mapFeaturedVideosClustersResponse(
	response: FeaturedVideosClustersResponse
): FeaturedVideosClusterSection {
	return {
		sectionTitle: response.sectionTitle?.trim() ?? '',
		clusters: (response.clusters ?? [])
			.map(mapFeaturedVideosCluster)
			.filter((cluster): cluster is FeaturedVideosClusterCard =>
				Boolean(cluster)
			)
	};
}

/** Serializes the backend-provided filters without changing their semantics. */
export function featuredVideosClusterFiltersToQuery(
	filters: FeaturedVideosClusterAppliedFilters
): Record<string, string> {
	const query: Record<string, string> = {};

	for (const [key, value] of Object.entries(filters)) {
		if (value === undefined || value === null) continue;
		query[key] = Array.isArray(value) ? value.join(',') : String(value);
	}

	return query;
}

export function useFetchShortsClusters() {
	const logger = useLogger('useFetchShortsClusters');
	const getCookieData = useCookieData();

	async function fetchClusters(
		referenceProperty?: string | null
	): Promise<FeaturedVideosClusterSection> {
		const normalizedReference = referenceProperty?.trim();
		if (!normalizedReference) return EMPTY_SECTION;

		const [result, error] = await nuxtPropertiesRepository.getShortsClusters(
			normalizedReference,
			getCookieData()
		);

		if (error || !result) {
			logger.error('Failed to fetch Shorts recommendation clusters', {
				...(error
					? extractErrorLogContext(error)
					: { error_message: 'No clusters result returned from repository' }),
				referenceProperty: normalizedReference
			});
			return EMPTY_SECTION;
		}

		return mapFeaturedVideosClustersResponse(result);
	}

	return { fetchClusters };
}
